/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar as CalendarIcon, Sparkles, Zap, ShieldAlert } from 'lucide-react';
import { cn } from '@/lib/utils';

export const URGENCY_OPTIONS = [
  { value: 'routine', labelKey: 'urgencyRoutine', icon: Sparkles, cls: 'badge-online' },
  { value: 'urgent', labelKey: 'urgencyUrgent', icon: Zap, cls: 'badge-waiting' },
  { value: 'emergency', labelKey: 'urgencyEmergency', icon: ShieldAlert, cls: 'badge-emergency' },
];

const daysAhead = (n) => {
  const arr = [];
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  for (let i = 0; i < n; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() + i);
    arr.push(d);
  }
  return arr;
};

export const isoDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Step 0 of the BookingWizard: pick a day, a time slot, and an urgency level.
 * Extracted from BookingWizard.jsx (Phase 8 refactor).
 */
const StepSlotPicker = ({
  selectedDate,
  onSelectDate,
  selectedSlot,
  onSelectSlot,
  slots,
  loadingSlots,
  urgency,
  onUrgencyChange,
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'it';
  const days = useMemo(() => daysAhead(14), []);

  return (
    <div className="space-y-5 animate-fade-slide">
      {/* Day picker */}
      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
          {t('bookPickDay')}
        </Label>
        <div className="mt-2 flex gap-2 overflow-x-auto pb-2 scrollbar-thin -mx-1 px-1">
          {days.map((d) => {
            const active = isoDate(d) === isoDate(selectedDate);
            return (
              <button
                key={isoDate(d)}
                onClick={() => onSelectDate(d)}
                className={cn(
                  'flex-shrink-0 w-[68px] h-[72px] rounded-2xl flex flex-col items-center justify-center transition-all border-2',
                  active
                    ? 'bg-primary text-primary-foreground border-primary shadow-elev-md scale-[1.02]'
                    : 'bg-card border-border/60 hover:border-primary/40 text-foreground',
                )}
                data-testid={`day-picker-${isoDate(d)}`}
              >
                <span
                  className={cn(
                    'text-[10px] uppercase font-semibold',
                    active ? 'opacity-90' : 'text-muted-foreground',
                  )}
                >
                  {d.toLocaleDateString(locale, { weekday: 'short' })}
                </span>
                <span className="text-xl font-display font-bold leading-none mt-1">{d.getDate()}</span>
                <span
                  className={cn(
                    'text-[10px] mt-0.5',
                    active ? 'opacity-90' : 'text-muted-foreground',
                  )}
                >
                  {d.toLocaleDateString(locale, { month: 'short' })}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Slot picker */}
      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
          {t('bookPickTime')}
        </Label>
        {loadingSlots ? (
          <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 mt-2">
            {[...Array(10)].map((_, i) => (
              <Skeleton key={`skel-${i}`} className="h-11" />
            ))}
          </div>
        ) : slots.length === 0 ? (
          <div className="mt-3 rounded-2xl border border-dashed border-border p-6 text-center">
            <CalendarIcon className="h-5 w-5 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">{t('bookNoSlotsForDay')}</p>
            <p className="text-xs text-muted-foreground mt-1">{t('bookTryAnotherDay')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 mt-2">
            {slots.map((s) => {
              const dt = new Date(s.start);
              const label = dt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
              const active = selectedSlot === s.start;
              return (
                <button
                  key={s.start}
                  onClick={() => onSelectSlot(s.start)}
                  className={cn(
                    'h-11 rounded-xl text-sm font-semibold transition-all border',
                    active
                      ? 'bg-primary text-primary-foreground border-primary shadow-elev-sm'
                      : 'bg-card border-border/60 hover:border-primary/40 text-foreground',
                  )}
                  data-testid={`slot-${s.start}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Urgency chips */}
      <div>
        <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
          {t('bookUrgency')}
        </Label>
        <div className="mt-2 grid grid-cols-3 gap-2">
          {URGENCY_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const active = urgency === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => onUrgencyChange(opt.value)}
                className={cn(
                  'rounded-2xl p-3 border-2 transition-all text-left',
                  active
                    ? 'border-primary shadow-elev-sm'
                    : 'border-border/60 hover:border-primary/30',
                )}
                data-testid={`urgency-${opt.value}`}
              >
                <span
                  className={cn(
                    'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider',
                    opt.cls,
                  )}
                >
                  <Icon className="h-3 w-3" /> {t(opt.labelKey)}
                </span>
                <p className="text-xs text-muted-foreground mt-1.5">{t(`${opt.labelKey}Desc`)}</p>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StepSlotPicker;
